import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';

import { CommonService } from '../../services/common.service';
import { ErrorService } from '../../services/error/error.service';
import { FileService } from '../../services/file.service';
import { LocalStorageService } from '../../services/local-storage.service';
import {
  DocumentDataObject,
  InvoiceDataObject,
  OfferDataObject,
  OrderDataObject,
  OrderProcessData,
  OrderProcessResponse,
} from '../../jurisdiction-overview/jurisdiction-overview.domain';
import { OrderProcessService } from './order-process.service';
import { ActivatedRoute, Router } from '@angular/router';
import { InstallationFlowType, MessageType } from '../../constants/app.constants';
import { SubmitService } from '../../services/submit.service';
import { AllDropdownKeys, UrlSegments } from '../jurisdiction.constants';
import { ChimneySweeper, GetChimneySweeperResponse, LookupOptionsData } from '../jurisdiction.domain';
import { NetzTranslateService } from '../../services/netz-translate.service';
import { CommunicationService } from '../../services/communication.service';
import { EnvironmentService } from 'src/app/services/environment.service';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { FileUpload } from 'primeng';
import { ReviewSectionStore } from 'src/app/+shared/components/review-section/review-section.store';
import { SectionStatus, SkExecutorAction } from 'src/app/+shared/components/review-section/review-section.domain';
import { map } from 'rxjs/operators';
import { OrderProcesStore } from 'src/app/+shared/stores/order-process.store';
import { UserService } from 'src/app/services/user.service';
import { ToastNotificationService } from 'src/app/+shared/services/toast-notification.service';

@Component({
  providers: [ConfirmationService, OrderProcessService],
  selector: 'app-order-process',
  styleUrls: ['./order-process.component.scss'],
  templateUrl: './order-process.component.html',
})
export class OrderProcessComponent extends DestroyableComponent implements OnInit {
  @Input() isReadOnly: boolean = false;
  @Input() isInstallationFlow: boolean = false;
  @Input() isConnectionFlow: boolean = false;
  @Input() beforeApplicationUpdate: () => Promise<void>;
  @Input() documentsForm: FormGroup;
  @Input() isPersonalDataValid: boolean;
  @Input() isTechnicalDataValid: boolean = true;

  @Output() executorActionEvent = new EventEmitter<any>();

  chimneySweeper: ChimneySweeper;
  chimneySweeperOptions: ChimneySweeper[];

  isLoading: boolean;

  offerData: OfferDataObject;
  invoiceData: InvoiceDataObject;
  orderData: OrderDataObject;

  orderProcessesKey: string = AllDropdownKeys.OrderProcess;
  orderProcesses: LookupOptionsData[] = this.commonService.getDropdownData(AllDropdownKeys.OrderProcess);

  orderProcessDetails: OrderProcessData;
  orderProcessFG: FormGroup;

  technicianId: string = this.localStorageService.getItem('technician_id');
  username: string = this.localStorageService.getItem('user_email');

  showRequestChangeBtn: boolean = this.environmentService.showRequestChangeBtn;
  showAcceptRejectApplicationBtns: boolean;
  applicationType: string = JSON.parse(localStorage.getItem('application_type'));
  showApplicationIdInOffer: boolean = this.environmentService.showApplicationIdInOffer;
  showOrderOfferText: boolean = this.environmentService.showOrderOfferText && this.isExternalUser;
  showCorrectOfferButton = false;
  skExecutorAction = SkExecutorAction;

  sectionType = {
    Offer: 'offer_submitted',
    Order: 'order_submitted',
    Invoice: 'invoice_submitted',
  };

  docIdTypes = {
    offer: 'offer_id',
    order: 'order_id',
    invoice: 'invoice_id',
  };
  areAllSectionsAccepted$: any;
  areAnySectionRejected$: any;
  isApplicationCompleted: boolean;
  areAnySectionUnreviewed$: any;
  isExecutorRole: boolean;
  isApplicationOwner: boolean;
  isBusinessPartner: boolean;

  private currentFlowType: string;

  constructor(
    private cdr: ChangeDetectorRef,
    private commonService: CommonService,
    private communicationService: CommunicationService,
    private confirmationService: ConfirmationService,
    private errorService: ErrorService,
    private fileService: FileService,
    private localStorageService: LocalStorageService,
    private orderProcessService: OrderProcessService,
    private router: Router,
    private submitService: SubmitService,
    private netzTranslateService: NetzTranslateService,
    private environmentService: EnvironmentService,
    private reviewSectionStore: ReviewSectionStore,
    private orderProcessStore: OrderProcesStore,
    private route: ActivatedRoute,
    private userService: UserService,
    private notificationsService: ToastNotificationService
  ) {
    super();
  }

  get disableSubmitOfferButton(): boolean {
    return (
      this.isReadOnly || this.orderProcessDetails.sk_executor_action !== SkExecutorAction.approve_offer || !this.offerData?.document?.length
    );
  }

  get disableSubmitOrderButton(): boolean {
    return (
      this.isReadOnly || this.orderProcessDetails.sk_executor_action !== SkExecutorAction.approve_order || !this.orderData?.document?.length
    );
  }

  get disableSubmitInvoiceButton(): boolean {
    return (
      this.isReadOnly ||
      this.orderProcessDetails.sk_executor_action !== SkExecutorAction.approve_invoice ||
      !this.invoiceData?.document?.length
    );
  }

  get isInternalUser(): boolean {
    return this.userService.isUserModeratorRole();
  }

  get isExternalUser(): boolean {
    return this.userService.isUserTechnicianRole() || this.userService.isUserCustomerRole();
  }

  get isChimneySweeper(): boolean {
    return this.userService.isChimneySweeper();
  }

  get standardOfferApplicationIdTitle(): string {
    return this.showApplicationIdInOffer
      ? this.netzTranslateService.translateString('overview.process-details.standard-offer-application-id-title', {
          value: this.orderProcessDetails.application_ext_id,
        })
      : null;
  }

  get showDownloadOfferSection(): boolean {
    return (
      this.isInternalUser ||
      (this.isExternalUser &&
        this.isConnectionFlow &&
        this.orderProcessDetails.businesspartner?.email?.toLocaleLowerCase() === this.username?.toLocaleLowerCase() && // allow customer access
        !['offer_created', 'application_wait_for_update', 'offer_rejected'].includes(this.orderProcessDetails.sk_orderprocess_status))
    );
  }

  get documentsFormValid(): boolean {
    for (const field in this.documentsForm?.controls) {
      if (this.isRequiredFormField(field) && this.documentsForm.get(field).value == '') {
        return false;
      }
    }
    return true;
  }

  private readonly enableDownloadOfferStatuses = [
    'offer_approved',
    'offer_accepted',
    'offer_submitted',
    'order_created',
    'order_approved_for_sap',
    'order_approved',
    'order_submitted',
    'invoice_created',
    'invoice_approved',
    'invoice_submitted',
    'invoice_paid',
    'payment_confirmed',
    'closed',
  ];
  get disableOffer(): boolean {
    return (
      !this.offerData?.document?.length ||
      (this.isExternalUser && !this.enableDownloadOfferStatuses.includes(this.orderProcessDetails.sk_orderprocess_status))
    );
  }

  isRequiredFormField(field: string) {
    const form_field = this.documentsForm.get(field);
    if (!form_field.validator) {
      return false;
    }

    const validator = form_field.validator({} as AbstractControl);
    return validator && validator.required;
  }

  ngOnInit(): void {
    this.preventLeak(this.route.queryParams).subscribe(params => {
      const onSuccess = () => {
        this.showAcceptRejectApplicationBtns =
          this.isInternalUser &&
          (this.applicationType === 'installation' || this.environmentService.showAcceptRejectApplicationBtns) &&
          ['application_accepted', 'application_rejected'].indexOf(this.orderProcessDetails?.sk_orderprocess_status) === -1;
      };

      this.getOrderProcessDetails(params.orderProcessId, onSuccess);
    });

    if (this.isChimneySweeper) {
      this.getChimneySweeperDetails();
    }

    const sectionReviews$ = this.reviewSectionStore.reviews;
    this.areAllSectionsAccepted$ = sectionReviews$.pipe(
      map(sectionReviews =>
        sectionReviews
          .filter(r => r.application_id == this.orderProcessDetails.application_id)
          .every(sectionReview => sectionReview.status === SectionStatus.approved)
      )
    );
    this.areAnySectionRejected$ = sectionReviews$.pipe(
      map(sectionReviews => sectionReviews.some(sectionReview => sectionReview.status === SectionStatus.rejected))
    );
    this.areAnySectionUnreviewed$ = sectionReviews$.pipe(
      map(sectionReviews => sectionReviews.some(sectionReview => sectionReview.status === SectionStatus.none))
    );
    this.currentFlowType = this.localStorageService.getItem('installation_flow');
    this.showCorrectOfferButton =
      this.environmentService.showCorrectOfferButton &&
      this.currentFlowType === InstallationFlowType.MainsConnection &&
      this.userService.isUserCustomerRole();
  }

  downloadDocument(document: DocumentDataObject): void {
    this.preventLeak(this.orderProcessService.readDocument(document.document_id)).subscribe(
      response => {
        this.errorService.throwErrorIfFailure(response, true);
        this.fileService.downloadDocument(response, document.filename);
      },
      error => this.errorService.throwErrorIfFailure(error, true)
    );
  }

  uploadDocument(
    file: File,
    docIdType: string,
    uploader: FileUpload,
    documentControl: AbstractControl,
    data?: { document?: DocumentDataObject[] }
  ) {
    documentControl.setValue(file.name);

    const documentId = data?.document?.find(() => true)?.document_id;
    const docId = (data || {})[docIdType];

    let uploadRequest = this.orderProcessService.uploadDocument(file, documentId, docIdType, docId);
    if (!documentId && !this.environmentService.createAutomaticOffer) {
      let docType = '';
      switch (docIdType) {
        case this.docIdTypes.offer:
          docType = 'offer';
          break;
        case this.docIdTypes.order:
          docType = 'order';
          break;
        case this.docIdTypes.invoice:
          docType = 'invoice';
          break;
      }
      uploadRequest = this.orderProcessService.uploadExampleDocument(file, docId, docIdType, docType);
    }

    this.preventLeak(uploadRequest).subscribe(() => {
      uploader.clear();
      this.route.queryParams.subscribe(params => {
        this.getOrderProcessDetails(params.orderProcessId);
      });
    });
  }

  updateOrderProcess(
    rejectOffer: boolean = false,
    status: string | null = null,
    options: { onlySave: boolean } = { onlySave: false }
  ): void {
    const updateOrderProcessFunction = () => {
      this.preventLeak(
        this.orderProcessService.updateOrderProcessData(this.orderProcessDetails.orderprocess_id, {
          sk_executor_action: this.getNextExecutorAction(offerAccepted, status),
        })
      ).subscribe(
        (response: OrderProcessResponse) => {
          this.errorService.throwErrorIfFailure(response, true);

          if (this.userService.isUserCustomerRole()) {
            this.postSuccessfulOfferUpdate(offerAccepted, status);
          } else {
            this.commonService.showHighlightedRow = this.orderProcessDetails.application_id;
            setTimeout(() => {
              this.isLoading = false;
              this.router.navigate(['/jurisdiction-overview']);
            }, 2000);
          }
        },
        error => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    };

    const offerAccepted = !rejectOffer;
    this.cdr.detectChanges();

    this.confirmationService.confirm({
      message: this.netzTranslateService.translateString(this.getMessageForDialog(status, offerAccepted)),
      accept: () => {
        this.isLoading = true;
        let beforeUpdate: () => Promise<void>;
        if (status === SkExecutorAction.update_application && this.beforeApplicationUpdate) {
          beforeUpdate = this.beforeApplicationUpdate;
        } else {
          beforeUpdate = () => Promise.resolve();
        }

        beforeUpdate().then(
          _ => {
            if (!options?.onlySave) {
              updateOrderProcessFunction();
            }
          },
          error => {
            this.isLoading = false;
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      },
      reject: () => this.confirmationService.close(),
    });
  }

  changeChimneySweeper() {
    this.confirmationService.confirm({
      message: this.netzTranslateService.translateString('overview.process-details.change-chimney-sweeper-text'),
      accept: () => {
        this.isLoading = true;
        const applicationId = this.orderProcessDetails?.application_id;
        this.communicationService
          .updateData(`${UrlSegments.InstallationApplication}${applicationId}`, {
            chimney_sweeper_id: this.chimneySweeper?.chimney_sweeper_id,
          })
          .subscribe(
            response => {
              this.isLoading = false;
              this.errorService.throwErrorIfFailure(response, true);
              this.confirmationService.close();
              this.router.navigate(['/jurisdiction-overview']);
            },
            error => {
              this.isLoading = false;
              this.errorService.throwErrorIfFailure(error, true);
            }
          );
      },
      reject: () => {
        this.confirmationService.close();
      },
    });
  }

  showSection(sectionType: string): boolean {
    const currentStatus = this.orderProcesses.filter(item => item.value === this.orderProcessDetails.sk_orderprocess_status);
    const submitSectionObject = this.orderProcesses.find(item => item.value === sectionType);

    return (
      (this.orderProcessDetails.businesspartner?.email?.toLocaleLowerCase() === this.username?.toLocaleLowerCase() && // allow customer access
        (currentStatus.length && Number(currentStatus[0].order)) >= (submitSectionObject ? Number(submitSectionObject.order) : 0)) ||
      this.isInternalUser
    );
  }

  private prepareForm(): void {
    if (this.isInstallationFlow) {
      return;
    }

    this.orderProcessFG = new FormGroup({
      standardOfferValue: new FormControl({
        value: this.offerData?.sk_offer_type === 'automatic' ? 'false' : 'true',
        disabled: this.isReadOnly,
      }),
      orderValue: new FormControl({
        value: this.orderData?.sk_offer_type === 'automatic' ? 'false' : 'true',
        disabled: this.isReadOnly,
      }),
      invoiceValue: new FormControl({
        value: this.invoiceData?.sk_offer_type === 'automatic' ? 'false' : 'true',
        disabled: this.isReadOnly,
      }),
      standardOfferDocument: new FormControl({
        value: this.offerData?.document?.length ? this.offerData.document[0].filename : '',
        disabled: true,
      }),
      orderDocument: new FormControl({
        value: this.orderData?.document?.length ? this.orderData.document[0].filename : '',
        disabled: true,
      }),
      invoiceDocument: new FormControl({
        value: this.invoiceData?.document?.length ? this.invoiceData.document[0].filename : '',
        disabled: true,
      }),
    });

    if (this.environmentService.showSapOfferAndOrderId) {
      this.orderProcessFG.addControl(
        'sapOfferId',
        new FormControl({
          value: this.offerData?.offer_no ?? '',
          disabled: true,
        })
      );
      this.orderProcessFG.addControl(
        'sapOrderId',
        new FormControl({
          value: this.orderData?.order_no ?? '',
          disabled: true,
        })
      );
    }
  }

  private getOrderProcessDetails(orderProcessId: string, onSuccess?: any): void {
    if (!orderProcessId) return;

    this.isLoading = true;
    this.preventLeak(this.orderProcessService.getOrderProcessData(orderProcessId)).subscribe(
      (response: OrderProcessResponse) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);

        if (response.orderprocess && response.orderprocess.length) {
          this.orderProcessDetails = response.orderprocess[0];
          this.orderProcessStore.setOrderProcessData(response.orderprocess[0]);
          this.isExecutorRole = this.userService.getUserRole().toLowerCase() === this.orderProcessDetails.executor;

          if (this.isConnectionFlow) {
            this.invoiceData = response.orderprocess[0].invoice;
            this.offerData = response.orderprocess[0].offer;
            this.orderData = response.orderprocess[0].order;
          }

          this.isApplicationOwner = this.orderProcessDetails.application_user_id === this.technicianId;
          this.isBusinessPartner =
            this.orderProcessDetails.businesspartner?.email?.toLocaleLowerCase() === this.username?.toLocaleLowerCase();

          onSuccess && onSuccess();
        }

        this.prepareForm();

        if (this.orderProcessDetails?.sk_executor_action) {
          this.executorActionEvent.emit(this.orderProcessDetails.sk_executor_action);
        }

        if (this.orderProcessDetails?.sk_orderprocess_status === 'application_rejected') {
          this.isApplicationCompleted = [
            'cs_application_accepted',
            'cs_application_rejected',
            'application_accepted',
            'application_rejected',
            'closed',
          ].includes(this.orderProcessDetails?.sk_orderprocess_status);
        }
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private getChimneySweeperDetails() {
    this.isLoading = true;
    this.communicationService.getData('chimneysweeper').subscribe(
      (response: GetChimneySweeperResponse) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        this.chimneySweeperOptions = response.chimneysweeper;
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error);
      }
    );
  }

  private postSuccessfulOfferUpdate(offerAccepted: boolean, status: string): void {
    if (this.environmentService.hideOrderProcessSubmittedMessage) {
      this.notificationsService.success(this.netzTranslateService.translateString('app.ui-messages.success'));
      this.commonService.showHighlightedRow = this.orderProcessDetails.application_id;
      setTimeout(() => {
        this.isLoading = false;
        this.router.navigate(['/jurisdiction-overview']);
      }, 2000);
    } else {
      let message = offerAccepted ? 'overview.process-details.accept-offer-text' : 'overview.process-details.reject-offer-text';
      if (status === SkExecutorAction.correct_offer) {
        message = 'overview.process-details.correct-offer-text';
      }
      this.submitService.setMessageText(message);
      this.submitService.setMessage(MessageType.DYNAMIC);
      this.isLoading = false;

      this.router.navigate(['/submitted']);
    }
  }

  private getMessageForDialog(status: string, offerAccepted: boolean): string {
    if (this.userService.isUserCustomerRole()) {
      if (this.showCorrectOfferButton && status === SkExecutorAction.correct_offer) {
        return 'overview.process-details.confirmation-text-correct-offer';
      }
      if (status === SkExecutorAction.update_application) {
        return 'overview.process-details.confirmation-text-update-application';
      }
      return offerAccepted
        ? 'overview.process-details.confirmation-text-accept-offer'
        : 'overview.process-details.confirmation-text-reject-offer';
    }

    if (offerAccepted) {
      if (this.isInstallationFlow) {
        return 'overview.process-details.confirmation-text-accept-application';
      }

      if (!this.disableSubmitOfferButton) {
        return 'overview.process-details.confirmation-text-offer';
      }

      if (!this.disableSubmitOrderButton) {
        return 'overview.process-details.confirmation-text-order';
      }
      if (!this.disableSubmitInvoiceButton) {
        return 'overview.process-details.confirmation-text-invoice';
      }

      if (
        this.orderProcessDetails.sk_executor_action === SkExecutorAction.accept_offer ||
        this.orderProcessDetails.sk_executor_action === SkExecutorAction.approve_offer
      ) {
        return 'overview.process-details.confirmation-text-accept-offer';
      }

      if (this.orderProcessDetails.sk_executor_action === SkExecutorAction.accept_application) {
        return 'overview.process-details.confirmation-text-accept-application';
      }
    }

    if (this.isInstallationFlow) {
      if (status) {
        return status === SkExecutorAction.request_update_application
          ? 'overview.process-details.confirmation-text-request-change'
          : 'overview.process-details.confirmation-text-update-application';
      }

      return 'overview.process-details.confirmation-text-reject-application';
    }

    if (this.isConnectionFlow && status === SkExecutorAction.request_update_application) {
      return 'overview.process-details.confirmation-text-request-change';
    }

    if (this.isConnectionFlow && status === SkExecutorAction.update_application) {
      return 'overview.process-details.confirmation-text-request-change-text2';
    }

    if (this.isConnectionFlow && !status) {
      return 'overview.process-details.confirmation-text-reject-application';
    }

    if (status === SkExecutorAction.application_rejected) {
      return 'overview.process-details.confirmation-text-reject-application';
    }

    if (
      this.orderProcessDetails.sk_executor_action === SkExecutorAction.accept_offer ||
      this.orderProcessDetails.sk_executor_action === SkExecutorAction.approve_offer
    ) {
      return 'overview.process-details.confirmation-text-update-application';
    }

    if (this.orderProcessDetails.sk_executor_action === SkExecutorAction.accept_application) {
      return 'overview.process-details.confirmation-text-reject-application';
    }

    if (status == this.skExecutorAction.reject_offer) {
      return 'overview.process-details.confirmation-text-reject-offer';
    }
  }

  private getNextExecutorAction(offerAccepted: boolean, status?: string): string {
    if (offerAccepted) {
      if (this.isInstallationFlow) {
        return this.isChimneySweeper ? SkExecutorAction.cs_application_accepted : SkExecutorAction.application_accepted;
      }

      if (status === SkExecutorAction.accept_offer) {
        return SkExecutorAction.accept_offer;
      }

      return this.orderProcessDetails.sk_executor_action;
    }

    if (status === SkExecutorAction.reject_offer) {
      return SkExecutorAction.reject_offer;
    }

    if (this.showCorrectOfferButton && status === SkExecutorAction.correct_offer) {
      return SkExecutorAction.correct_offer;
    }

    if (status === SkExecutorAction.request_update_application) {
      return SkExecutorAction.request_update_application;
    }

    if (status === SkExecutorAction.update_application) {
      return SkExecutorAction.update_application;
    }

    if (status === SkExecutorAction.application_rejected) {
      return SkExecutorAction.application_rejected;
    }

    if (status === SkExecutorAction.cs_request_update_application) {
      return SkExecutorAction.cs_request_update_application;
    }

    if (this.isInstallationFlow) {
      if (this.isChimneySweeper) {
        return SkExecutorAction.cs_application_rejected;
      } else {
        return status ? status : SkExecutorAction.application_rejected;
      }
    }

    return SkExecutorAction.reject_offer;
  }
}
